import React, { useState, useRef } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../../config/api';
import uploadService from '../../../services/UploadService';
import styles from './SaleHistoryEditor.module.scss';

/**
 * ✍️ SALE HISTORY EDITOR - Editor para Adicionar Entradas ao Histórico
 * 
 * Features:
 * - Textarea com formatação HTML básica
 * - Upload de imagem com preview
 * - Toolbar com ações rápidas de formatação
 * - Validação de conteúdo
 * - Loading states
 * 
 * @param {number} saleId - ID da venda
 * @param {Function} onSuccess - Callback após adicionar entrada
 */
const SaleHistoryEditor = ({ saleId, onSuccess }) => {
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);

  /**
   * 📝 Aplicar formatação ao texto
   */
  const applyFormatting = (format) => {
    const textarea = document.getElementById('historyTextarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = description.substring(start, end);
    
    let formattedText = '';
    
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText || 'texto'}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText || 'texto'}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText || 'texto'}</u>`;
        break;
      case 'list':
        formattedText = `\n<ul>\n  <li>${selectedText || 'item'}</li>\n</ul>\n`;
        break;
      case 'link':
        const url = prompt('Digite a URL:');
        if (url) {
          formattedText = `<a href="${url}" target="_blank">${selectedText || 'link'}</a>`;
        }
        break;
      default:
        return;
    }
    
    const newText = description.substring(0, start) + formattedText + description.substring(end);
    setDescription(newText);
    
    // Refocar no textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  /**
   * 🖼️ Lidar com seleção de imagem
   */
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');

    try {
      // Validar arquivo
      const validation = uploadService.validateFile(file, {
        type: 'image',
        allowedFormats: uploadService.validImageFormats
      });

      if (!validation.valid) {
        throw new Error(validation.error);
      }

      setImageFile(file);

      // Criar preview local
      const preview = await uploadService.createPreview(file);
      setImagePreview(preview);

    } catch (err) {
      console.error('❌ Erro ao selecionar imagem:', err);
      setError(err.message);
      setImageFile(null);
      setImagePreview(null);
    }
  };

  /**
   * 📤 Fazer upload da imagem
   */
  const handleUploadImage = async () => {
    if (!imageFile) return;

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Simular progresso
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Upload
      const result = await uploadService.uploadImage(imageFile, {
        onProgress: (progress) => setUploadProgress(progress)
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      setImageUrl(result.url);
      console.log('✅ Imagem enviada:', result.url);

    } catch (err) {
      console.error('❌ Erro no upload:', err);
      setError(err.message || 'Erro ao enviar imagem');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  /**
   * 🗑️ Remover imagem
   */
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * 💾 Salvar entrada no histórico
   */
  const handleSave = async () => {
    if (!description.trim() && !imageUrl) {
      setError('Adicione uma descrição ou imagem');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action_type: 'note_added',
          description: description.trim(),
          image_url: imageUrl || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao adicionar entrada');
      }

      console.log('✅ Entrada adicionada ao histórico');

      // Limpar formulário
      setDescription('');
      handleRemoveImage();

      // Callback de sucesso
      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      console.error('❌ Erro ao salvar:', err);
      setError(err.message || 'Erro ao adicionar entrada ao histórico');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.editor}>
      {/* Toolbar de Formatação */}
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => applyFormatting('bold')}
          title="Negrito"
        >
          <Icon icon="mdi:format-bold" />
        </button>
        
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => applyFormatting('italic')}
          title="Itálico"
        >
          <Icon icon="mdi:format-italic" />
        </button>
        
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => applyFormatting('underline')}
          title="Sublinhado"
        >
          <Icon icon="mdi:format-underline" />
        </button>

        <div className={styles.toolbarDivider} />
        
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => applyFormatting('list')}
          title="Lista"
        >
          <Icon icon="mdi:format-list-bulleted" />
        </button>
        
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => applyFormatting('link')}
          title="Link"
        >
          <Icon icon="mdi:link-variant" />
        </button>
      </div>

      {/* Textarea */}
      <Form.Group className={styles.textareaGroup}>
        <Form.Control
          id="historyTextarea"
          as="textarea"
          rows={5}
          placeholder="Descreva o que aconteceu... (suporta HTML básico)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={styles.textarea}
        />
        <small className={styles.hint}>
          <Icon icon="mdi:information" />
          Você pode usar tags HTML: &lt;strong&gt;, &lt;em&gt;, &lt;u&gt;, &lt;a&gt;, &lt;ul&gt;, &lt;li&gt;
        </small>
      </Form.Group>

      {/* Upload de Imagem */}
      <div className={styles.imageSection}>
        <div className={styles.imageSectionHeader}>
          <label>
            <Icon icon="mdi:image-plus" />
            Imagem (opcional)
          </label>
        </div>

        {!imagePreview ? (
          <div className={styles.uploadArea}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className={styles.fileInput}
              id="imageInput"
            />
            <label htmlFor="imageInput" className={styles.uploadLabel}>
              <Icon icon="mdi:cloud-upload" />
              <span>Clique para selecionar uma imagem</span>
              <small>JPG, PNG, GIF - Máximo 5MB</small>
            </label>
          </div>
        ) : (
          <div className={styles.imagePreviewContainer}>
            <img 
              src={imagePreview} 
              alt="Preview" 
              className={styles.imagePreview}
            />
            
            <div className={styles.imageActions}>
              {!imageUrl ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUploadImage}
                  disabled={uploading}
                  className={styles.uploadBtn}
                >
                  {uploading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:cloud-upload" className="me-2" />
                      Enviar Imagem
                    </>
                  )}
                </Button>
              ) : (
                <div className={styles.uploadSuccess}>
                  <Icon icon="mdi:check-circle" />
                  <span>Imagem enviada com sucesso!</span>
                </div>
              )}
              
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveImage}
                disabled={uploading}
              >
                <Icon icon="mdi:delete" />
              </Button>
            </div>

            {uploading && uploadProgress > 0 && (
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Erro */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <Icon icon="mdi:alert-circle" className="me-2" />
          {error}
        </Alert>
      )}

      {/* Botões de Ação */}
      <div className={styles.editorActions}>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || (!description.trim() && !imageUrl) || uploading}
          className={styles.saveBtn}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Salvando...
            </>
          ) : (
            <>
              <Icon icon="mdi:content-save" className="me-2" />
              Adicionar ao Histórico
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SaleHistoryEditor;

